/**
 * @file SettingsHubColumn.tsx
 * @description Колонка разделов «Настроек» слева от выбранного раздела
 * (phase19.31, SC15; page-analysis-c §2 макет (а), uiux-design §5).
 *
 * Только от 1280px. Ширина 240 (15rem), закреплена при прокрутке страницы и
 * прокручивается сама, если разделов много. Группа — подпись 12/500
 * обычным регистром; пункт — 44px, 14/500, значок слева, без описания;
 * выбранный — мягкая подложка и полоса слева, как пункт бокового меню;
 * длинное название переносится, а не обрезается.
 * Данные — те же, что у списка до 1280 (`visibleSettingsHub`).
 */

import { Link } from "react-router";

import type { SettingsHubGroup } from "../../lib/shop-nav-registry";
import { itemHref } from "../../lib/shop-nav";
import { cn } from "@/lib/utils";

interface Props {
  hub: readonly SettingsHubGroup[];
  slug: string;
  /** Раздел, открытый справа. */
  currentId: string | null;
}

export function SettingsHubColumn({ hub, slug, currentId }: Props) {
  return (
    <nav
      aria-label="Разделы настроек"
      data-testid="settings-hub-column"
      // Шапка 72px (+ полоса входа под сотрудником) и отступ содержимого 32px.
      className="sticky top-[calc(4.5rem+var(--shell-top,0px)+2rem)] flex max-h-[calc(100vh-4.5rem-var(--shell-top,0px)-4rem)] flex-col gap-0.5 overflow-y-auto"
    >
      {hub.map((group, index) => (
        <div key={group.id} className="flex flex-col gap-0.5">
          <div className={cn("px-3 pb-1 text-xs font-medium text-on-surface-variant", index > 0 && "pt-4")}>
            {group.label}
          </div>
          {group.items.map((item) => {
            const Icon = item.icon;
            const selected = item.id === currentId;
            return (
              <Link
                key={item.id}
                to={itemHref(slug, item)}
                aria-current={selected ? "page" : undefined}
                className={cn(
                  "flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium outline-none transition-colors",
                  "focus-visible:ring-2 focus-visible:ring-ring [&_svg]:size-5 [&_svg]:shrink-0",
                  selected
                    ? "bg-accent text-accent-foreground [box-shadow:inset_3px_0_0_var(--primary)]"
                    : "text-on-surface hover:bg-surface-low",
                )}
              >
                <Icon aria-hidden className={selected ? undefined : "text-on-surface-variant"} />
                <span className="min-w-0 break-words">{item.label}</span>
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
